import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import * as sdk from "matrix-js-sdk";
import { IndexedDBCryptoStore } from "matrix-js-sdk";
import {
    MeetingFieldGrid,
    MeetingPage,
    MeetingPageCard,
    MeetingPanel,
    MeetingPanelText,
    MeetingPanelTitle,
    MeetingPrimaryButton,
    MeetingSecondaryButton,
    MeetingStoreBadge,
    MeetingStoreLink,
    MeetingStoreLinks,
    MeetingStatusCard,
    MeetingTextInput,
} from "src/components";
import AppleStoreBadge from "src/img/new/apple.svg";
import GooglePlayBadge from "src/img/new/Google.svg";
import CallLobby from "src/scenes/Chat/components/call/CallLobby";
import ChatCallPanel from "src/scenes/Chat/components/ChatCallPanel";
import { ChatContext } from "src/scenes/Chat/ChatContext";
import { useCallWindowRuntime } from "src/scenes/Chat/hooks/useCallWindowRuntime";
import { useMeetingCallNotes } from "src/scenes/Meet/useMeetingCallNotes";
import { UserContext } from "src/scenes/App/UserContext";
import { UserKeys } from "src/constants/userDetails";
import {
    getAuthenticatedMeetingByToken,
    getGuestMeetingSession,
    getPublicMeeting,
    getMeetingApiBaseUrl,
    GuestMeetingSession,
    joinInternalMeetingByToken,
    leaveInternalMeeting,
    MeetingDetail,
    leaveGuestMeeting,
    PublicMeeting,
    requestGuestMeetingSession,
    startMeeting,
} from "src/api/meetingsApi";

const APP_STORE_URL = "https://apps.apple.com/au/app/me-business-employee-wellness/id6504783839";
const PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=com.onpoint_holdings.mebusinessapp";
const GUEST_IDENTITY_KEY = "meBusinessMeetingGuestIdentity";
const MEETING_NOT_STARTED_MESSAGE = "The meeting has not started.";
const SESSION_STATUS_ERROR = "Unable to check your join request. We’ll keep trying automatically.";

const formatScheduledStart = (meeting: PublicMeeting): string | null => {
    if (!meeting.scheduledStart) {
        return null;
    }
    return new Intl.DateTimeFormat(undefined, {
        dateStyle: "full",
        timeStyle: "short",
        timeZone: meeting.timezone,
    }).format(new Date(meeting.scheduledStart));
};

const getRequestedJoinScheduleNotice = (meeting: PublicMeeting): string | null => {
    if (meeting.status === "active") {
        return null;
    }
    const currentTime = Date.now();
    if (meeting.scheduledStart !== null && new Date(meeting.scheduledStart).getTime() > currentTime) {
        return "This meeting has not started yet. Your request was sent, and the host can admit you early.";
    }
    if (meeting.scheduledEnd !== null && new Date(meeting.scheduledEnd).getTime() < currentTime) {
        return "The scheduled meeting time has passed. Your request was sent, and the host can still admit you while the meeting link remains active.";
    }
    return null;
};

const getBrowserIdentity = (): string => {
    const existing = localStorage.getItem(GUEST_IDENTITY_KEY);
    if (existing) {
        return existing;
    }
    const identity = window.crypto.randomUUID().replace(/-/g, "");
    localStorage.setItem(GUEST_IDENTITY_KEY, identity);
    return identity;
};

type GuestRouteParams = { meetingToken: string };
type AccessMode = "checking" | "internal" | "guest";
type PortalUser = Record<string, unknown>;

const getApiErrorMessage = (error: unknown, fallback: string): string => {
    if (typeof error === "object" && error !== null && "getApiErrorDetails" in error) {
        const apiError = error as { getApiErrorDetails?: () => { message?: string } };
        return apiError.getApiErrorDetails?.().message || fallback;
    }
    return fallback;
};

const getApiErrorStatus = (error: unknown): number | undefined => {
    if (typeof error === "object" && error !== null && "getStatus" in error) {
        const apiError = error as { getStatus?: () => number | undefined };
        return apiError.getStatus?.();
    }
    return undefined;
};

const getPortalDisplayName = (user: PortalUser | undefined): string => [
    user?.[UserKeys.FIRST_NAME],
    user?.[UserKeys.LAST_NAME],
].filter(Boolean).join(" ");

type GuestLiveMeetingProps = {
    displayName: string;
    meeting: PublicMeeting;
    mediaSettings: Record<string, unknown>;
    session: Required<Pick<GuestMeetingSession, "matrix">> & GuestMeetingSession;
    onLeft: () => void;
};

function GuestCallRuntime({ matrixClient, displayName, meeting, mediaSettings, session, onLeft }: GuestLiveMeetingProps & { matrixClient: sdk.MatrixClient }) {
    const bridge = useMemo(() => ({
        matrixClient,
        currentUser: {},
        headers: null,
        reportStatus: (status: { status?: string }) => {
            if (status.status === "ended") {
                onLeft();
            }
        },
        removeCall: () => undefined,
    }), [matrixClient, onLeft]);
    const descriptor = useMemo(() => ({
        type: "inbound",
        meetingGuest: true,
        callId: `meeting-${meeting.meetingToken}`,
        chat: {
            uuid: meeting.meetingToken,
            name: meeting.title,
            groupName: meeting.title,
            isGroupChat: true,
            matrixRoomId: session.matrix.roomId,
            matrixRoom: matrixClient.getRoom(session.matrix.roomId),
            users: [{ matrixUserId: matrixClient.getUserId(), firstName: displayName, lastName: "" }],
        },
    }), [displayName, matrixClient, meeting.meetingToken, meeting.title, session.matrix.roomId]);
    const runtime = useCallWindowRuntime({ bridge, callToken: meeting.meetingToken, descriptor });
    const meetingNotes = useMeetingCallNotes(
        matrixClient,
        session.matrix.roomId
    );
    const connectedRef = useRef(false);
    const meetingEndedRef = useRef(false);

    useEffect(() => {
        if (!connectedRef.current && descriptor.chat.matrixRoomId) {
            connectedRef.current = true;
            runtime.connect(mediaSettings).catch(() => {
                connectedRef.current = false;
            });
        }
    }, [descriptor.chat.matrixRoomId, mediaSettings, runtime.connect]);

    useEffect(() => {
        if (!session.sessionToken) {
            return undefined;
        }
        const checkMeetingStatus = async (): Promise<void> => {
            try {
                const currentSession = await getGuestMeetingSession(meeting.meetingToken, session.sessionToken as string);
                if (
                    !meetingEndedRef.current
                    && ["declined", "left", "removed"].includes(currentSession.status)
                ) {
                    meetingEndedRef.current = true;
                    await runtime.hangup();
                    onLeft();
                }
            } catch {
                // A temporary polling failure must not interrupt an active guest call.
            }
        };
        const intervalId = window.setInterval(checkMeetingStatus, 2000);
        return () => window.clearInterval(intervalId);
    }, [meeting.meetingToken, onLeft, runtime.hangup, session.sessionToken]);

    return (
        <ChatCallPanel
            dedicatedRuntime
            callState={runtime.callState}
            callNotes={meetingNotes.notes}
            isSendingCallNote={meetingNotes.isSending}
            onAddCallNote={meetingNotes.addNote}
            onDeleteCallNote={meetingNotes.deleteNote}
            onEditCallNote={meetingNotes.editNote}
            matrixClient={matrixClient}
            currentChat={descriptor.chat}
            currentUser={{}}
            currentMatrixUserId={matrixClient.getUserId()}
            callLobby={{ isOpen: false, chat: descriptor.chat, popupBlocked: false }}
            isSendingCallAction={runtime.isConnecting}
            screenShareIsActive={runtime.screenShareIsActive}
            localStream={runtime.localStream}
            localScreenShareStream={runtime.localScreenShareStream}
            remoteStreams={runtime.remoteStreams}
            onFocusWindow={() => window.focus()}
            onCancelLobby={() => undefined}
            onStartFromLobby={() => undefined}
            onSetLocalAudioEnabled={runtime.setLocalAudioEnabled}
            onSetLocalMicrophoneDevice={runtime.setLocalMicrophoneDevice}
            onSetLocalVideoEnabled={runtime.setLocalVideoEnabled}
            onSetLocalCameraDevice={runtime.setLocalCameraDevice}
            onSetLocalSpeakerDevice={runtime.setLocalSpeakerDevice}
            onToggleScreenShare={runtime.toggleScreenShare}
            onSetLocalBackgroundEffect={runtime.setLocalBackgroundEffect}
            onAnswerFromLobby={() => undefined}
            onHangup={async () => {
                if (session.sessionToken) {
                    await leaveGuestMeeting(meeting.meetingToken, session.sessionToken).catch(() => undefined);
                }
                runtime.hangup();
                onLeft();
            }}
            onReject={runtime.cancel}
        />
    );
}

function GuestLiveMeeting(props: GuestLiveMeetingProps) {
    const [matrixClient, setMatrixClient] = useState<sdk.MatrixClient | null>(null);
    const [error, setError] = useState("");
    const clientRef = useRef<sdk.MatrixClient | null>(null);

    useEffect(() => {
        let disposed = false;
        const initialise = async () => {
            const credentials = props.session.matrix;
            const cryptoDatabasePrefix = `meeting_rust_crypto_${credentials.userId}_${credentials.deviceId}`;
            const cryptoStore = new IndexedDBCryptoStore(
                window.indexedDB,
                `meeting_crypto_${credentials.userId}_${credentials.deviceId}`
            );
            const client = sdk.createClient({
                baseUrl: getMeetingApiBaseUrl(),
                accessToken: credentials.accessToken,
                userId: credentials.userId,
                deviceId: credentials.deviceId,
                cryptoStore,
            });
            clientRef.current = client;
            try {
                await client.initRustCrypto({
                    useIndexedDB: true,
                    cryptoDatabasePrefix,
                });
                client.startClient({ initialSyncLimit: 20, disablePresence: true });
                await new Promise<void>((resolve, reject) => {
                    const timeout = window.setTimeout(() => reject(new Error("Meeting room sync timed out.")), 15000);
                    const onSync = (state: string) => {
                        if (state === "PREPARED" || state === "SYNCING") {
                            window.clearTimeout(timeout);
                            client.off(sdk.ClientEvent.Sync, onSync);
                            resolve();
                        }
                    };
                    client.on(sdk.ClientEvent.Sync, onSync);
                });
                if (!disposed) {
                    setMatrixClient(client);
                }
            } catch (initialiseError) {
                client.stopClient();
                if (!disposed) {
                    setError(initialiseError instanceof Error ? initialiseError.message : "Unable to initialise the encrypted meeting.");
                }
            }
        };
        initialise();
        return () => {
            disposed = true;
            clientRef.current?.stopClient();
            clientRef.current = null;
        };
    }, []);

    if (error) {
        return <MeetingPage><MeetingPageCard><MeetingStatusCard $tone="danger">{error}</MeetingStatusCard></MeetingPageCard></MeetingPage>;
    }
    if (!matrixClient) {
        return <MeetingPage><MeetingPageCard><MeetingStatusCard>Preparing your encrypted meeting…</MeetingStatusCard></MeetingPageCard></MeetingPage>;
    }
    return <GuestCallRuntime {...props} matrixClient={matrixClient} />;
}

type AuthenticatedLiveMeetingProps = {
    meeting: MeetingDetail;
    mediaSettings: Record<string, unknown>;
    matrixClient: sdk.MatrixClient;
    headers: unknown;
    user: PortalUser;
    onLeft: () => void;
};

function AuthenticatedLiveMeeting({ meeting, mediaSettings, matrixClient, headers, user, onLeft }: AuthenticatedLiveMeetingProps) {
    const displayName = getPortalDisplayName(user);
    const matrixRoomId = meeting.matrixRoomId || meeting.occurrence.matrixRoomId || "";
    const bridge = useMemo(() => ({
        matrixClient,
        currentUser: user,
        headers,
        reportStatus: (status: { status?: string }) => {
            if (status.status === "ended") {
                onLeft();
            }
        },
        removeCall: () => undefined,
    }), [headers, matrixClient, onLeft, user]);
    const descriptor = useMemo(() => ({
        type: "inbound",
        meetingUuid: meeting.meetingUuid,
        callId: `meeting-${meeting.meetingToken}`,
        chat: {
            uuid: meeting.meetingUuid,
            name: meeting.title,
            groupName: meeting.title,
            isGroupChat: true,
            matrixRoomId,
            matrixRoom: matrixClient.getRoom(matrixRoomId),
            meetingCanManage: meeting.canManage,
            meetingHostEmployeeUuid: meeting.hostEmployeeUuid,
            users: [{
                matrixUserId: matrixClient.getUserId(),
                firstName: user[UserKeys.FIRST_NAME] || displayName,
                lastName: user[UserKeys.LAST_NAME] || "",
            }],
        },
    }), [displayName, matrixClient, matrixRoomId, meeting.meetingToken, meeting.meetingUuid, meeting.title, user]);
    const runtime = useCallWindowRuntime({ bridge, callToken: meeting.meetingToken, descriptor });
    const meetingNotes = useMeetingCallNotes(matrixClient, matrixRoomId);
    const connectedRef = useRef(false);

    useEffect(() => {
        if (!connectedRef.current && matrixRoomId) {
            connectedRef.current = true;
            runtime.connect(mediaSettings).catch(() => {
                connectedRef.current = false;
            });
        }
    }, [matrixRoomId, mediaSettings, runtime.connect]);

    return (
        <ChatCallPanel
            dedicatedRuntime
            callState={runtime.callState}
            callNotes={meetingNotes.notes}
            isSendingCallNote={meetingNotes.isSending}
            onAddCallNote={meetingNotes.addNote}
            onDeleteCallNote={meetingNotes.deleteNote}
            onEditCallNote={meetingNotes.editNote}
            matrixClient={matrixClient}
            currentChat={{ ...descriptor.chat, meetingUuid: meeting.meetingUuid }}
            currentUser={user}
            currentMatrixUserId={matrixClient.getUserId()}
            callLobby={{ isOpen: false, chat: descriptor.chat, popupBlocked: false }}
            isSendingCallAction={runtime.isConnecting}
            screenShareIsActive={runtime.screenShareIsActive}
            localStream={runtime.localStream}
            localScreenShareStream={runtime.localScreenShareStream}
            remoteStreams={runtime.remoteStreams}
            onFocusWindow={() => window.focus()}
            onCancelLobby={() => undefined}
            onStartFromLobby={() => undefined}
            onSetLocalAudioEnabled={runtime.setLocalAudioEnabled}
            onSetLocalMicrophoneDevice={runtime.setLocalMicrophoneDevice}
            onSetLocalVideoEnabled={runtime.setLocalVideoEnabled}
            onSetLocalCameraDevice={runtime.setLocalCameraDevice}
            onSetLocalSpeakerDevice={runtime.setLocalSpeakerDevice}
            onToggleScreenShare={runtime.toggleScreenShare}
            onSetLocalBackgroundEffect={runtime.setLocalBackgroundEffect}
            onAnswerFromLobby={() => undefined}
            onHangup={async () => {
                await leaveInternalMeeting(meeting.meetingUuid).catch(() => undefined);
                runtime.hangup();
                onLeft();
            }}
            onReject={runtime.cancel}
        />
    );
}

export default function GuestMeetingPage() {
    const { meetingToken } = useParams<GuestRouteParams>();
    const user = useContext(UserContext) as PortalUser | undefined;
    const chatContext = useContext(ChatContext) as {
        headers?: unknown;
        isInitialSyncComplete?: boolean;
        matrixClient?: sdk.MatrixClient | null;
    } | undefined;
    const isPortalUser = Boolean(user?.[UserKeys.TOKEN]);
    const portalDisplayName = getPortalDisplayName(user);
    const [meeting, setMeeting] = useState<PublicMeeting | null>(null);
    const [authenticatedMeeting, setAuthenticatedMeeting] = useState<MeetingDetail | null>(null);
    const [joinedInternalMeeting, setJoinedInternalMeeting] = useState<MeetingDetail | null>(null);
    const [accessMode, setAccessMode] = useState<AccessMode>(isPortalUser ? "checking" : "guest");
    const [stage, setStage] = useState<"landing" | "lobby" | "waiting" | "live">("landing");
    const [displayName, setDisplayName] = useState("");
    const [password, setPassword] = useState("");
    const [session, setSession] = useState<GuestMeetingSession | null>(null);
    const [mediaSettings, setMediaSettings] = useState<Record<string, unknown>>({});
    const [error, setError] = useState("");
    const [warning, setWarning] = useState("");
    const [loading, setLoading] = useState(true);
    const [openingLobby, setOpeningLobby] = useState(false);
    const warningRef = useRef<HTMLDivElement | null>(null);
    const sessionPollFailureCountRef = useRef(0);
    const scheduledStart = meeting ? formatScheduledStart(meeting) : null;
    const requestedJoinScheduleNotice = meeting ? getRequestedJoinScheduleNotice(meeting) : null;

    useEffect(() => {
        if (warning) {
            warningRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
        }
    }, [warning]);

    useEffect(() => {
        getPublicMeeting(meetingToken)
            .then(setMeeting)
            .catch(apiError => setError(getApiErrorMessage(apiError, "This meeting link is invalid or unavailable.")))
            .finally(() => setLoading(false));
    }, [meetingToken]);

    useEffect(() => {
        if (!isPortalUser) {
            setAccessMode("guest");
            setAuthenticatedMeeting(null);
            return;
        }
        let disposed = false;
        setAccessMode("checking");
        getAuthenticatedMeetingByToken(meetingToken)
            .then(nextMeeting => {
                if (!disposed) {
                    setAuthenticatedMeeting(nextMeeting);
                    setAccessMode("internal");
                }
            })
            .catch(apiError => {
                if (disposed) {
                    return;
                }
                const status = getApiErrorStatus(apiError);
                if (status === 401 || status === 403 || status === 404) {
                    setAuthenticatedMeeting(null);
                    setAccessMode("guest");
                    return;
                }
                setError(getApiErrorMessage(apiError, "Unable to check your Me Business meeting access."));
                setAccessMode("guest");
            });
        return () => {
            disposed = true;
        };
    }, [isPortalUser, meetingToken]);

    useEffect(() => {
        if (stage !== "waiting" || !session?.sessionToken) {
            return undefined;
        }
        const poll = window.setInterval(() => {
            getGuestMeetingSession(meetingToken, session.sessionToken as string)
                .then(nextSession => {
                    sessionPollFailureCountRef.current = 0;
                    setError(previous => previous === SESSION_STATUS_ERROR ? "" : previous);
                    setSession(previous => ({ ...previous, ...nextSession } as GuestMeetingSession));
                    if (nextSession.status === "admitted" && nextSession.matrix) {
                        setStage("live");
                    } else if (nextSession.status === "declined" || nextSession.status === "removed") {
                        setError("The host declined or ended this join request.");
                    }
                })
                .catch(apiError => {
                    const status = getApiErrorStatus(apiError);
                    if (status === 401 || status === 403 || status === 404) {
                        setError("The guest session expired. Please request to join again.");
                        return;
                    }
                    sessionPollFailureCountRef.current += 1;
                    if (sessionPollFailureCountRef.current >= 3) {
                        setError(SESSION_STATUS_ERROR);
                    }
                });
        }, 2500);
        return () => window.clearInterval(poll);
    }, [meetingToken, session?.sessionToken, stage]);

    const requestJoin = async (settings: Record<string, unknown>) => {
        if (accessMode === "internal") {
            if (!chatContext?.matrixClient || !chatContext.isInitialSyncComplete || !authenticatedMeeting) {
                setError("Preparing your Me Business calling session. Please try again shortly.");
                setWarning("");
                return;
            }
            setError("");
            setWarning("");
            try {
                if (authenticatedMeeting.canManage && authenticatedMeeting.occurrence.status !== "active") {
                    await startMeeting(authenticatedMeeting.meetingUuid);
                }
                const nextMeeting = await joinInternalMeetingByToken(meetingToken);
                setAuthenticatedMeeting(nextMeeting);
                setJoinedInternalMeeting(nextMeeting);
                setMediaSettings(settings);
                setStage("live");
            } catch (apiError) {
                const message = getApiErrorMessage(apiError, "Unable to join this meeting with your Me Business account.");
                if (message === MEETING_NOT_STARTED_MESSAGE) {
                    setWarning("The host has not started this meeting yet. Please wait for the host, then try again.");
                } else {
                    setError(message);
                }
            }
            return;
        }
        if (displayName.trim().length < 2) {
            setError("Enter your name before requesting to join.");
            return;
        }
        setError("");
        try {
            const latestMeeting = await getPublicMeeting(meetingToken);
            setMeeting(latestMeeting);
            if (latestMeeting.passwordRequired && !password) {
                setError("Enter the meeting password from your invitation email.");
                return;
            }
            const nextSession = await requestGuestMeetingSession(
                meetingToken,
                displayName.trim(),
                password,
                getBrowserIdentity()
            );
            setMediaSettings(settings);
            setSession(nextSession);
            setStage(nextSession.status === "admitted" && nextSession.matrix ? "live" : "waiting");
        } catch (apiError) {
            setError(getApiErrorMessage(apiError, "Unable to request access to this meeting."));
        }
    };

    const openLobby = async (): Promise<void> => {
        setError("");
        setWarning("");
        setOpeningLobby(true);
        try {
            const latestMeeting = await getPublicMeeting(meetingToken);
            setMeeting(latestMeeting);
            if (accessMode === "internal") {
                const latestAuthenticatedMeeting = await getAuthenticatedMeetingByToken(meetingToken);
                setAuthenticatedMeeting(latestAuthenticatedMeeting);
            }
            setStage("lobby");
        } catch (apiError) {
            setError(getApiErrorMessage(apiError, "Unable to load the meeting setup."));
        } finally {
            setOpeningLobby(false);
        }
    };

    const returnToSetup = async (): Promise<void> => {
        if (session?.sessionToken) {
            await leaveGuestMeeting(meetingToken, session.sessionToken).catch(() => undefined);
        }
        setSession(null);
        setError("");
        setWarning("");
        setStage("lobby");
    };

    if (stage === "live" && joinedInternalMeeting && chatContext?.matrixClient && user) {
        return (
            <AuthenticatedLiveMeeting
                meeting={joinedInternalMeeting}
                mediaSettings={mediaSettings}
                matrixClient={chatContext.matrixClient}
                headers={chatContext.headers}
                user={user}
                onLeft={() => {
                    setJoinedInternalMeeting(null);
                    setStage("landing");
                }}
            />
        );
    }

    if (stage === "live" && meeting && session?.matrix) {
        return (
            <GuestLiveMeeting
                displayName={displayName}
                meeting={meeting}
                mediaSettings={mediaSettings}
                session={session as GuestLiveMeetingProps["session"]}
                onLeft={() => {
                    setSession(null);
                    setStage("landing");
                }}
            />
        );
    }

    return (
        <MeetingPage>
            <MeetingPageCard>
                <MeetingPanelTitle $dark>{loading ? "Loading meeting…" : meeting?.title || "Join Me Meets"}</MeetingPanelTitle>
                {meeting && (
                    <MeetingPanelText $dark>
                        Hosted by {meeting.hostName}{meeting.businessName ? ` · ${meeting.businessName}` : ""} · {meeting.connectedParticipantCount} connected
                    </MeetingPanelText>
                )}
                {scheduledStart && stage !== "waiting" && (
                    <MeetingPanelText $dark>Scheduled for {scheduledStart}</MeetingPanelText>
                )}
                {meeting && ["cancelled", "expired"].includes(meeting.status) && (
                    <MeetingStatusCard $tone="danger" style={{ marginTop: 16 }}>
                        This meeting has {meeting.status === "cancelled" ? "been cancelled" : "expired"} and can no longer be joined.
                    </MeetingStatusCard>
                )}
                {error && <MeetingStatusCard $tone="danger" style={{ marginTop: 16 }}>{error}</MeetingStatusCard>}
                {warning && stage !== "lobby" && (
                    <MeetingStatusCard ref={warningRef} $tone="warning" role="status" style={{ marginTop: 16 }}>
                        <strong>Meeting not started</strong><br />{warning}
                    </MeetingStatusCard>
                )}
                {stage === "landing" && meeting?.passwordRequired && accessMode === "guest" && (
                    <MeetingStatusCard style={{ marginTop: 16 }}>
                        <strong>Password required</strong><br />
                        Check your invitation email, then enter the meeting password below.
                        <MeetingTextInput
                            aria-label="Meeting Password"
                            type="password"
                            value={password}
                            onChange={({ target }) => setPassword(target.value)}
                            placeholder="Password from invitation email"
                            style={{ marginTop: 12 }}
                        />
                    </MeetingStatusCard>
                )}
                {stage === "landing" && meeting && !["cancelled", "expired"].includes(meeting.status) && (
                    <MeetingFieldGrid style={{ marginTop: 24 }}>
                        <MeetingPanel $dark>
                            <MeetingPanelTitle $dark>Continue in Browser</MeetingPanelTitle>
                            <MeetingPanelText $dark>
                                {accessMode === "internal"
                                    ? `Continue as ${portalDisplayName || "your Me Business account"}.`
                                    : "Join directly in this browser. No Me Business account is required."}
                            </MeetingPanelText>
                            <MeetingPrimaryButton
                                type="button"
                                disabled={openingLobby || accessMode === "checking"}
                                onClick={openLobby}
                            >
                                {accessMode === "checking"
                                    ? "Checking Account…"
                                    : openingLobby ? "Opening Setup…" : "Continue in Browser"}
                            </MeetingPrimaryButton>
                        </MeetingPanel>
                        <MeetingPanel $dark>
                            <MeetingPanelTitle $dark>Me Business Account</MeetingPanelTitle>
                            <MeetingPanelText $dark>
                                Already have a Me Business account? Join from the Web Business Portal or download the app to access your meetings. If you do not have an account, continue in this browser as a guest.
                            </MeetingPanelText>
                            <MeetingStoreLinks>
                                <MeetingStoreLink
                                    aria-label="Download Me Business from the Apple App Store"
                                    href={APP_STORE_URL}
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    <MeetingStoreBadge src={AppleStoreBadge} alt="Download on the App Store" />
                                </MeetingStoreLink>
                                <MeetingStoreLink
                                    aria-label="Download Me Business from Google Play"
                                    href={PLAY_STORE_URL}
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    <MeetingStoreBadge src={GooglePlayBadge} alt="Get it on Google Play" />
                                </MeetingStoreLink>
                            </MeetingStoreLinks>
                        </MeetingPanel>
                    </MeetingFieldGrid>
                )}
                {stage === "lobby" && meeting && (
                    <div style={{ marginTop: 24 }}>
                        {warning && (
                            <MeetingStatusCard ref={warningRef} $tone="warning" role="status" style={{ marginBottom: 16 }}>
                                <strong>Meeting not started</strong><br />{warning}
                            </MeetingStatusCard>
                        )}
                        {accessMode === "internal" ? (
                            <MeetingStatusCard style={{ marginBottom: 16 }}>
                                Joining as <strong>{portalDisplayName || "a Me Business user"}</strong>. Same-company users do not need the meeting password or host approval.
                            </MeetingStatusCard>
                        ) : meeting.passwordRequired && (
                            <MeetingStatusCard style={{ marginBottom: 16 }}>
                                <strong>Password required</strong><br />
                                Check your invitation email for the meeting password, then enter it below.
                            </MeetingStatusCard>
                        )}
                        {accessMode !== "internal" && (
                            <MeetingFieldGrid style={{ marginBottom: 16 }}>
                                <MeetingTextInput
                                    aria-label="Your Name"
                                    value={displayName}
                                    onChange={({ target }) => setDisplayName(target.value)}
                                    placeholder="Your Name"
                                />
                                {meeting.passwordRequired && (
                                    <MeetingTextInput
                                        aria-label="Meeting Password"
                                        autoComplete="current-password"
                                        type="password"
                                        value={password}
                                        onChange={({ target }) => setPassword(target.value)}
                                        placeholder="Password from invitation email"
                                    />
                                )}
                            </MeetingFieldGrid>
                        )}
                        <CallLobby
                            actionLabel={accessMode === "internal"
                                ? authenticatedMeeting?.canManage && authenticatedMeeting.occurrence.status !== "active"
                                    ? "Start Meeting"
                                    : "Join Meeting"
                                : "Request to Join"}
                            chat={{ name: meeting.title, uuid: meeting.meetingToken }}
                            currentChat={{ name: meeting.title, uuid: meeting.meetingToken }}
                            getUserDisplayName={() => accessMode === "internal" ? portalDisplayName : displayName}
                            isSendingCallAction={false}
                            onCancel={() => setStage("landing")}
                            onStart={requestJoin}
                        />
                        <MeetingSecondaryButton
                            type="button"
                            onClick={() => {
                                setError("");
                                setWarning("");
                                setStage("landing");
                            }}
                        >
                            Back
                        </MeetingSecondaryButton>
                    </div>
                )}
                {stage === "waiting" && (
                    <div style={{ marginTop: 24 }}>
                        {requestedJoinScheduleNotice && (
                            <MeetingStatusCard style={{ marginBottom: 12 }}>
                                {requestedJoinScheduleNotice}
                            </MeetingStatusCard>
                        )}
                        <MeetingStatusCard>
                            <strong>Waiting to Join</strong><br />The meeting organiser will let you in shortly. If the host is away, your request will wait until they return.
                        </MeetingStatusCard>
                        <MeetingSecondaryButton type="button" onClick={returnToSetup} style={{ marginTop: 12 }}>
                            Back to Setup
                        </MeetingSecondaryButton>
                    </div>
                )}
            </MeetingPageCard>
        </MeetingPage>
    );
}
